import type {
  AddSubIssueMutationMutation,
  AddSubIssueInput,
} from '@github-ui/server-persisted-queries/types/addSubIssueMutation'
import type {
  RemoveSubIssueMutationMutation,
  RemoveSubIssueInput,
} from '@github-ui/server-persisted-queries/types/removeSubIssueMutation'
import {
  addSubIssueMutation as addSubIssueQueryId,
  removeSubIssueMutation as removeSubIssueQueryId,
} from '@github-ui/server-persisted-queries'
import {reactFetchJSON} from '@github-ui/verified-fetch'

type GraphQLError = {readonly message?: string}
type GraphQLResponse<T> = {readonly data?: T | null; readonly errors?: readonly GraphQLError[]}

type RawAddSubIssuePayload = NonNullable<AddSubIssueMutationMutation['addSubIssue']>
type RawRemoveSubIssuePayload = NonNullable<RemoveSubIssueMutationMutation['removeSubIssue']>

export type AddSubIssuePayload = Omit<RawAddSubIssuePayload, 'issue' | 'subIssue'> & {
  issue: NonNullable<RawAddSubIssuePayload['issue']>
  subIssue: NonNullable<RawAddSubIssuePayload['subIssue']>
}
export type RemoveSubIssuePayload = Omit<RawRemoveSubIssuePayload, 'issue' | 'subIssue'> & {
  issue: NonNullable<RawRemoveSubIssuePayload['issue']>
  subIssue: NonNullable<RawRemoveSubIssuePayload['subIssue']>
}

type AddSubIssueRequestInput = Pick<AddSubIssueInput, 'subIssueId' | 'replaceParent'>
type RemoveSubIssueRequestInput = Pick<RemoveSubIssueInput, 'subIssueId'>

function mutationPath(owner: string, repo: string, number: number, queryId: string): string {
  return `/${owner}/${repo}/issues/${number}/sub_issues?queryId=${encodeURIComponent(queryId)}`
}

function firstErrorMessage(errors: readonly GraphQLError[] | undefined, fallback: string): string {
  return errors?.find(error => error.message)?.message ?? fallback
}

export async function addSubIssueRequest({
  owner,
  repo,
  number,
  input,
}: {
  owner: string
  repo: string
  number: number
  input: AddSubIssueRequestInput
}): Promise<AddSubIssuePayload> {
  const response = await reactFetchJSON(mutationPath(owner, repo, number, addSubIssueQueryId), {
    method: 'POST',
    body: {input},
  })
  if (!response.ok) throw new Error(`Add sub-issue request failed (${response.status})`)

  const json = (await response.json()) as GraphQLResponse<AddSubIssueMutationMutation>
  const payload = json.data?.addSubIssue
  if (!payload) {
    throw new Error(firstErrorMessage(json.errors, 'Add sub-issue mutation returned no payload'))
  }
  if (payload.errors.length) throw new Error(firstErrorMessage(payload.errors, 'Failed to add sub-issue'))
  const subIssue = payload.subIssue
  if (!subIssue) throw new Error('Add sub-issue mutation returned no sub-issue')
  const issue = payload.issue
  if (!issue?.subIssuesSummary) throw new Error('Add sub-issue mutation returned no summary')
  return {...payload, issue, subIssue}
}

export async function removeSubIssueRequest({
  owner,
  repo,
  number,
  input,
}: {
  owner: string
  repo: string
  number: number
  input: RemoveSubIssueRequestInput
}): Promise<RemoveSubIssuePayload> {
  const response = await reactFetchJSON(mutationPath(owner, repo, number, removeSubIssueQueryId), {
    method: 'DELETE',
    body: {input},
  })
  if (!response.ok) throw new Error(`Remove sub-issue request failed (${response.status})`)

  const json = (await response.json()) as GraphQLResponse<RemoveSubIssueMutationMutation>
  const payload = json.data?.removeSubIssue
  if (!payload) {
    throw new Error(firstErrorMessage(json.errors, 'Remove sub-issue mutation returned no payload'))
  }
  if (payload.errors.length) throw new Error(firstErrorMessage(payload.errors, 'Failed to remove sub-issue'))
  const subIssue = payload.subIssue
  if (!subIssue) throw new Error('Remove sub-issue mutation returned no sub-issue')
  const issue = payload.issue
  if (!issue?.subIssuesSummary) throw new Error('Remove sub-issue mutation returned no summary')
  return {...payload, issue, subIssue}
}
